import { FC, MouseEvent, ReactNode, useMemo } from 'react';
import DefaultLayout from '@/layouts/Default/default';
import { motion } from 'framer-motion';
import {
  Heading2,
  Paragraph,
  StyledCallout,
  ImpactStatement,
  KeyBulletList,
} from '@/components/Blog/BlogComponents';
import { Seo } from '@/components/SEO/Seo';
import { pageMetadata } from '@/config/pageMetadata';
import { siteMetadata } from '@/config/seo';

type Principle = {
  title: string;
  description: ReactNode;
};

const worthYourTimePrinciples: Principle[] = [
  {
    title: 'we are what the dev agencies will look like in a couple years',
    description: (
      <>
        A decade of AI experience and 2 years of battle scars have cemented agentic systems into our day-to-day. They scaffold code
        and tests, draft PRs, chase logs, and surface regressions while humans enforce standards and product taste. We break the
        iron triangle—<strong>we accomplish the work of ten with two, in a sixth of the time, while elevating quality.</strong>
      </>
    ),
  },
  {
    title: 'Quality without chaos (design-led, outcome-driven).',
    description: (
      <>
        Our standard resource is a right-/left-brain pair: product + engineering with real taste. We’re design-led and
        business-outcome obsessed—typed, test-backed code behind preview URLs, design-system primitives, perf/accessibility
        budgets, observability from Day 2, and a real release checklist (acceptance notes, rollback plan). <strong>Minimal
        enablement. Maximum signal.</strong>
      </>
    ),
  },
  {
    title: 'Ether: project ops built for how we actually build.',
    description: (
      <>
        We outgrew generic PM tools, so we built <strong>Ether</strong>. To you, scope feels like a <strong>shopping cart</strong>—you pick and approve the work; we
        ship daily; <strong>living documentation</strong> (decisions, PR links, screenshots, runbooks) is written as we build. Agents are wired in for
        status, QA nudges, and evidence capture. No spreadsheets, no mystery—just clear choices and predictable finish.
      </>
    ),
  },
  {
    title: 'AI-first partner (cut the noise, keep the wins).',
    description: (
      <>
        Everyone’s forming “AI committees.” Most added noise; many gave up. We’re the opposite: two years of agentic in production,
        with the scars. We separate slideware from systems, choose production-safe patterns, and make AI multiplicative across
        dev, QA, ops, and support.
      </>
    ),
  },
  {
    title: 'we are now proven.',
    description: (
      <>
        <strong>~1,100 features</strong> across <strong>9 hero projects</strong> in roughly 12 months. Repeated 10-day sprints (15–25 AC-tight features); 2-week surges
        (25–40 features or prototype → prod). We proved it for a full year under SOC-tight teams and moving goalposts. <strong>Now we’re taking it to
        market—get the advantage before everyone else catches up.</strong>
      </>
    ),
  },
];

const executionPoints = [
  {
    icon: 'bolt',
    title: 'Built for impossible timelines',
    text: 'Ship launch-critical features in days with pods that already know the patterns, the tests, and the deployment path.',
  },
  {
    icon: 'insights',
    title: 'Evidence on demand',
    text: 'Daily drops include metrics, links, and review-ready artifacts—your leadership team sees momentum, not status theater.',
  },
  {
    icon: 'security',
    title: 'Enterprise-grade safeguards',
    text: 'Typed stacks, observability from Day 2, and runbooks ensure the slice we ship can live in production without babysitting.',
  },
  {
    icon: 'diversity_3',
    title: 'Executives stay in the loop',
    text: 'One intake, one approver, predictable rituals. You sign off once; we keep the room informed without adding meetings.',
  },
];

const slingshotPackages = [
  {
    title: 'Accelerator S',
    headline: 'Win back the release you keep slipping.',
    featureRange: '15–25 features',
    price: '$18k',
    timeline: '10 days',
    details: [
      'Production-ready slice with daily agent-assisted drops.',
      'Review notes, PR links, and approvals bundled in Ether.',
      'Ideal when headcount freezes or approvals bottleneck delivery.',
    ],
  },
  {
    title: 'Surge M',
    headline: 'Turn prototype energy into live product.',
    featureRange: '25–40 features',
    price: '$22k',
    timeline: '14 days',
    details: [
      'Hardening, docs, and demo loops for exec-ready launches.',
      'Ether tracks decisions, QA evidence, and stakeholder sign-offs.',
      'Perfect for board reviews, migrations, or roadmap resets.',
    ],
  },
  {
    title: 'Flagship XL',
    headline: 'Anchor bet with enterprise polish.',
    featureRange: '40–70 features',
    price: '$60k',
    timeline: '4 weeks',
    details: [
      'Two production pushes plus on-site workshop and KT.',
      'Specialists layer in perf, infra, and AI systems as needed.',
      'Exec readouts, SLOs, and a full delivery index included.',
    ],
  },
];

const entryPointPackages = [
  {
    title: 'Prototype-as-Product',
    price: '$8k',
    description:
      'Working, branded prototype aligned to your design system for investor or sales demos. Pair with a pitch-deck package for a sharp vision.',
    timeline: '~10 days',
  },
  {
    title: 'Deck Templates',
    price: '$8k',
    description:
      'Choose between 80–100 branded templates or a filled 20–30 slide narrative covering story and visuals.',
    timeline: '~10 days',
  },
  {
    title: 'AI Committee Seat',
    price: '$12k/mo (prepaid)',
    description:
      'Advisor-in-Residence membership. We set a 90-day roadmap, pick production-safe agents, wire guardrails and metrics, and cut the noise.',
    timeline: 'Monthly engagement',
  },
];

const linkCards = [
  {
    title: 'See how Slingshot shipped 140 features in 6 weeks',
    description: 'Dive into our Work hub and explore delivery indexes from recent accelerators.',
    href: '/work',
    image: '/case-studies/case-study.png',
    cta: 'Browse work',
  },
  {
    title: 'Explore Mach — our enterprise AI launchpad',
    description: 'Layer AI agents into your workflows with a dedicated pod and intelligence roadmap.',
    href: '/services/mach',
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80',
    cta: 'Visit Mach',
  },
  {
    title: 'Grab the Rapid SaaS checklist',
    description: 'A five-part framework we use to scope, ship, and review every accelerator engagement.',
    href: '/blog',
    image: 'https://images.unsplash.com/photo-1522252234503-e356532cafd5?auto=format&fit=crop&w=1400&q=80',
    cta: 'Read the guide',
  },
];

const serviceFaqs = [
  {
    question: 'How fast do Turbo Labs pods typically deliver?',
    answer:
      'Our accelerator pods ship 15–70 production-ready features in 10–28 days, pairing senior builders with agentic automations so approvals are the only bottleneck.',
  },
  {
    question: 'What is included in Ether, your project operations layer?',
    answer:
      'Ether provides scope shopping carts, daily drops with evidence, QA nudges, and living documentation—so stakeholders review links, screenshots, and playbooks without status theater.',
  },
  {
    question: 'Can you support enterprise governance and security requirements?',
    answer:
      'Yes. Typed stacks, observability on day two, acceptance notes, rollback plans, and SOC-friendly controls are standard so regulated teams can push code without rework.',
  },
];


const ServicesPage: FC = () => {
  const seo = pageMetadata.services;
  const servicesStructuredData = useMemo(() => {
    const description = seo.description ?? siteMetadata.defaultDescription;
    const canonicalUrl = `${siteMetadata.siteUrl}${seo.canonicalPath ?? '/services'}`;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'Turbo Labs Rapid SaaS Pods',
        description,
        url: canonicalUrl,
        provider: {
          '@type': 'Organization',
          name: siteMetadata.siteName,
          url: siteMetadata.siteUrl,
        },
        areaServed: {
          '@type': 'Country',
          name: 'United States',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Slingshot accelerator packages',
          itemListElement: slingshotPackages.map((pkg, index) => ({
            '@type': 'Offer',
            name: pkg.title,
            position: index + 1,
            description: `${pkg.headline} (${pkg.featureRange}, ${pkg.timeline})`,
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: serviceFaqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ];
  }, [seo.canonicalPath, seo.description, slingshotPackages, serviceFaqs]);
  const handleContactClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (typeof window !== 'undefined') {
      // @ts-expect-error custom drawer event dispatched globally
      window.dispatchEvent(new Event('open-contact'));
    }
  };

  return (
    <DefaultLayout>
      <Seo {...seo} structuredData={servicesStructuredData} />
      <div className="w-full min-h-screen bg-white text-zinc-900 dark:bg-zinc-950 dark:text-zinc-50">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-16"
        >
          <section className="space-y-6">
            <p className="text-[12px] uppercase tracking-[0.3em] text-zinc-500 dark:text-zinc-400">
              Rapid SaaS engagements
            </p>
            <h1 className="font-creato text-[clamp(38px,6.2vw,70px)] leading-[1.05] tracking-tight text-zinc-900 dark:text-zinc-50">
              Burn through your roadmap.
            </h1>
            <h2 className="font-creato text-[clamp(24px,4.4vw,38px)] leading-[1.08] text-zinc-900/80 dark:text-zinc-100/85">
              Give your prospect the feature they mentioned two days ago.
            </h2>
            <Paragraph>
              Rapid SaaS is how you weaponize speed. Two-person pods, agentic workflows, and daily evidence mean you ship production-grade slices while everyone else is still framing the problem.
            </Paragraph>
          </section>

          <ImpactStatement content="Velocity only matters when the slice ships with receipts: agents clear the path, pods commit the code, and your stakeholders see proof every single day." />

          <Heading2>Why Turbo Labs is worth your calendar invite</Heading2>
          <Paragraph>
            We’ve productized the operating system other agencies will spend the next few years chasing. Here’s what that actually
            feels like from day one.
          </Paragraph>
          <ol className="mt-12 space-y-10">
            {worthYourTimePrinciples.map((principle, index) => (
              <li key={principle.title} className="flex flex-col gap-4">
                <span className="text-sm uppercase tracking-[0.4em] text-zinc-400 dark:text-zinc-500">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-montreal text-2xl lowercase leading-tight text-zinc-900 dark:text-zinc-50">
                    {principle.title}
                  </h3>
                  <p className="mt-3 max-w-3xl text-base leading-relaxed text-zinc-700 dark:text-zinc-300">
                    {principle.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <KeyBulletList
            heading="How that advantage shows up inside a sprint"
            description="Every engagement runs on the same playbook—tight intake, daily evidence, and production-level guardrails."
            points={executionPoints}
          />

          <Heading2 description="Pick the window, bring us the acceptance criteria, and we handle the rest—agents, pods, and receipts included.">
            Choose your sprint window
          </Heading2>
          <div className="grid gap-6 lg:gap-8 md:grid-cols-2 xl:grid-cols-3">
            {slingshotPackages.map((pkg) => (
              <div
                key={pkg.title}
                className="flex h-full flex-col rounded-[28px] border border-zinc-200 bg-white p-7 text-zinc-900 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-md dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              >
                <div className="space-y-5">
                  <span className="text-xs uppercase tracking-[0.28em] text-zinc-500 dark:text-zinc-400">
                    {pkg.title}
                  </span>
                  <h3 className="text-3xl font-light leading-snug text-zinc-900 dark:text-zinc-100">
                    {pkg.headline}
                  </h3>
                  <ul className="space-y-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
                    {pkg.details.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto space-y-6 pt-6">
                  <div className="text-4xl font-light text-zinc-900 dark:text-zinc-100">
                    {pkg.featureRange}
                  </div>
                  <div className="flex items-end justify-between border-t border-zinc-200 pt-5 dark:border-zinc-700">
                    <div>
                      <div className="text-2xl font-light text-zinc-900 dark:text-zinc-100">{pkg.price}</div>
                      <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 dark:text-zinc-400">
                        {pkg.timeline}
                      </div>
                    </div>
                    <a
                      href="/contact"
                      onClick={handleContactClick}
                      className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 py-2 text-sm font-light text-zinc-900 transition hover:bg-zinc-100 dark:border-zinc-500 dark:bg-transparent dark:text-zinc-100 dark:hover:bg-zinc-800"
                    >
                      Reserve slot
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Heading2 description="Smaller packages that plug into the same pod and get momentum fast.">
            Entry points
          </Heading2>
          <div className="grid gap-5 md:grid-cols-3">
            {entryPointPackages.map((pack) => (
              <div
                key={pack.title}
                className="flex h-full flex-col justify-between rounded-[24px] border border-zinc-200 bg-white p-6 text-zinc-900 shadow-sm transition hover:-translate-y-1 hover:shadow-md dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              >
                <div className="space-y-4">
                  <span className="text-xs uppercase tracking-[0.24em] text-zinc-500 dark:text-zinc-400">
                    {pack.timeline}
                  </span>
                  <h3 className="text-xl font-light leading-snug">
                    {pack.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
                    {pack.description}
                  </p>
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-zinc-200 pt-4 dark:border-zinc-700">
                  <span className="text-base font-light text-zinc-900 dark:text-zinc-100">
                    {pack.price}
                  </span>
                  <a
                    href="/contact"
                    onClick={handleContactClick}
                    className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 py-2 text-sm font-light text-zinc-900 transition hover:bg-zinc-100 dark:border-zinc-500 dark:bg-transparent dark:text-zinc-100 dark:hover:bg-zinc-800"
                  >
                    Start
                  </a>
                </div>
              </div>
            ))}
          </div>

          <StyledCallout
            text="Hold a 10-day slot next week."
            accentText="15-minute fit check today. We bring the pod, the agentic factory, and the plan."
            accentColor="border-brand3 dark:border-brand3"
          >
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="/contact"
                onClick={handleContactClick}
                className="inline-flex items-center justify-center rounded-full bg-brand3 px-6 py-3 text-sm font-light text-brand-white transition hover:bg-brand4"
              >
                Book the fit check
              </a>
              <a
                href="mailto:hello@turbolabs.ai"
                className="inline-flex items-center justify-center rounded-full border border-brand2/40 px-6 py-3 text-sm font-light text-brand1 transition hover:bg-brand5/80 dark:border-brand4/60 dark:text-brand-white dark:hover:bg-brand2/60"
              >
                Email us directly
              </a>
            </div>
          </StyledCallout>

          <Heading2 description="Deeper dives if you want to see our receipts, process, or frameworks before we start.">
            Keep exploring
          </Heading2>
          <div className="space-y-6">
            {linkCards.map((card) => (
              <a
                key={card.title}
                href={card.href}
                className="group flex flex-col overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-2 hover:shadow-md dark:border-zinc-700 dark:bg-zinc-900"
              >
                <div className="flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between">
                  <div className="flex-1 space-y-4">
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-brand3/80">
                      Featured
                    </span>
                    <h3 className="text-3xl font-light leading-snug text-brand1 dark:text-brand-white">
                      {card.title}
                    </h3>
                    <p className="max-w-xl text-sm leading-relaxed text-brand1/70 dark:text-brand-white/80">
                      {card.description}
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-light text-brand3 transition group-hover:translate-x-1">
                      {card.cta}
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 7h7v7" />
                      </svg>
                    </span>
                  </div>
                  <div className="relative h-48 w-full overflow-hidden rounded-2xl md:h-40 md:w-72">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
    </DefaultLayout>
  );
};

export default ServicesPage;
